import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Form, Input, Switch } from 'antd'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useClassifierServiceGetClassifierEndpoint,
  useClassifierServiceGetClassifierEndpointKey,
  useClassifierServiceUpdateClassifierEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { message, SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { CategorySelect } from '..'
import { useStyles } from '../styles'

export const GeneralDetails = () => {
  const { styles } = useStyles()

  const { t } = useLocales()
  const [form] = Form.useForm()
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data } = useClassifierServiceGetClassifierEndpoint({
    id: String(id)
  })

  const { mutate, isPending } = useClassifierServiceUpdateClassifierEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [useClassifierServiceGetClassifierEndpointKey]
      })
      message.success(t('common.save_success'))
      navigate(paths.classifiers.root)
    },
    onError: (e: ApiError) => {
      form?.setFields(formErrorHandler(e.body.errors))
    }
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    mutate({ id: String(id), requestBody: values })
  }

  const onCancel = () => {
    form.resetFields()
    navigate(paths.classifiers.root)
  }

  useEffect(() => {
    form.setFieldsValue(data)
  }, [data])

  return (
    <Form layout="vertical" form={form} requiredMark={false}>
      <Flex vertical gap={24} className={styles.wrapper}>
        <Card title={t('classifiers.title')}>
          <Form.Item name="name">
            <Input placeholder={t('classifiers.title')} />
          </Form.Item>
        </Card>
        <Card title={t('classifiers.category')}>
          <Form.Item name="category">
            <CategorySelect hasDefault={false} />
          </Form.Item>
        </Card>
        <Card title={t('classifiers.hasKey')}>
          <Form.Item name="hasKey" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Card>
        <Card title={t('classifiers.description')}>
          <Form.Item name="description">
            <Input.TextArea
              placeholder={t('classifiers.description')}
              rows={5}
            />
          </Form.Item>
        </Card>
      </Flex>

      <Flex gap={16} align="center" justify="center">
        <Button onClick={onCancel}>{t('common.cancel')}</Button>

        <SaveConfirm
          title={`${t('common.save')}?`}
          onConfirm={onSubmit}
          okButtonProps={{ loading: isPending }}
        >
          <Button type="primary">{t('common.save')}</Button>
        </SaveConfirm>
      </Flex>
    </Form>
  )
}
